import AsyncStorage from '@react-native-async-storage/async-storage';
import * as FileSystem from 'expo-file-system';
import { Buffer } from 'buffer';
import { 
    ApiRequest, 
    fetchDataFromAPITyped, 
    isSuccessful 
} from './api-requests';
import { 
    AttachmentObj, 
    fileActions 
} from '../models/file-handler-model';

/**
 * FUNCTION: uploadFileMultipartWeb
 * PURPOSE: Upload file using browser FormData (Web only)
 * @param formData - FormData containing file and metadata
 * @returns Promise with upload result
 */
export const uploadFileMultipartWeb = async (
    formData: FormData
): Promise<{ documentid?: number; error?: string }> => {
    try {
        // Get authentication token
        const jwtToken = await AsyncStorage.getItem('token');
        const username = await AsyncStorage.getItem('username');
        const IpAddress = await AsyncStorage.getItem('IpAddress');

        // Make multipart/form-data request
        const response = await fetch(
            `${process.env.REACT_NATIVE_API_BASE_URL}filehandler/upload`,
            {
                method: 'POST',
                headers: {
                    'authorization': 'Bearer ' + jwtToken,
                    'x-username': `${username}`,
                    'x-ip-address': `${IpAddress}`,
                    // Don't set Content-Type - browser will set it with boundary
                },
                body: formData,
            }
        );

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Upload failed:', response.status, errorText);
            return { error: `Upload failed: ${response.statusText}` };
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Error in uploadFileMultipartWeb:', error);
        return { error: `Exception during upload: ${error}` };
    }
};

/**
 * FUNCTION: uploadFileMultipartNative
 * PURPOSE: Upload file using expo-file-system (Native only)
 * @param fileUri - URI of the file to upload
 * @param parameters - Upload parameters (documentname, checksum, etc.)
 * @returns Promise with upload result
 */
export const uploadFileMultipartNative = async (
    fileUri: string,
    parameters: Record<string, string>
): Promise<{ documentid?: number; error?: string; status: number }> => {
    try {
        // Get authentication token
        const jwtToken = await AsyncStorage.getItem('token');
        const username = await AsyncStorage.getItem('username');
        const IpAddress = await AsyncStorage.getItem('IpAddress');

        // Use FileSystem.uploadAsync for multipart upload
        const uploadResult = await FileSystem.uploadAsync(
            `${process.env.REACT_NATIVE_API_BASE_URL}filehandler/upload`,
            fileUri,
            {
                httpMethod: 'POST',
                uploadType: FileSystem.FileSystemUploadType.MULTIPART,
                fieldName: 'file',
                parameters,
                headers: {
                    'authorization': 'Bearer ' + jwtToken,
                    'x-username': username || '',
                    'x-ip-address': IpAddress || '',
                },
            }
        );

        if (uploadResult.status >= 200 && uploadResult.status < 300) {
            const result = JSON.parse(uploadResult.body);
            return { ...result, status: uploadResult.status };
        } else {
            console.error('Upload failed:', uploadResult.status, uploadResult.body);
            return { 
                error: `Upload failed: ${uploadResult.status}`, 
                status: uploadResult.status 
            };
        }
    } catch (error) {
        console.error('Error in uploadFileMultipartNative:', error);
        return { error: `Exception during upload: ${error}`, status: 0 };
    }
};

/**
 * FUNCTION: uploadFileWithProgressWeb
 * PURPOSE: Upload file with progress tracking using XMLHttpRequest (Web only)
 * @param formData - FormData containing file and metadata
 * @param onProgress - Progress callback (0-100)
 * @returns Promise with upload result
 */
export const uploadFileWithProgressWeb = async (
    formData: FormData,
    onProgress?: (percent: number) => void
): Promise<{ documentid?: number; error?: string }> => {
    try {
        // Get authentication token
        const jwtToken = await AsyncStorage.getItem('token');
        const username = await AsyncStorage.getItem('username');
        const IpAddress = await AsyncStorage.getItem('IpAddress');

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable && onProgress) {
                    const percent = (e.loaded / e.total) * 100;
                    onProgress(Math.round(percent));
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const result = JSON.parse(xhr.responseText);
                        resolve(result);
                    } catch (e) {
                        reject(new Error('Failed to parse response'));
                    }
                } else {
                    resolve({ error: `Upload failed: ${xhr.statusText}` });
                }
            });

            xhr.addEventListener('error', () => {
                reject(new Error('Network error during upload'));
            });

            xhr.addEventListener('abort', () => {
                reject(new Error('Upload aborted'));
            });

            xhr.open('POST', `${process.env.REACT_NATIVE_API_BASE_URL}filehandler/upload`);
            xhr.setRequestHeader('authorization', 'Bearer ' + jwtToken);
            xhr.setRequestHeader('x-username', username || '');
            xhr.setRequestHeader('x-ip-address', IpAddress || '');
            
            xhr.send(formData);
        });
    } catch (error) {
        console.error('Error in uploadFileWithProgressWeb:', error);
        return { error: `Exception during upload: ${error}` };
    }
};

/**
 * FUNCTION: uploadFileWithProgressNative
 * PURPOSE: Upload file with progress tracking (Native only)
 * @param fileUri - URI of the file to upload
 * @param parameters - Upload parameters
 * @param onProgress - Progress callback (0-100)
 * @returns Promise with upload result
 */
export const uploadFileWithProgressNative = async (
    fileUri: string,
    parameters: Record<string, string>,
    onProgress?: (percent: number) => void
): Promise<{ documentid?: number; error?: string; status: number }> => {
    try {
        // Get authentication token
        const jwtToken = await AsyncStorage.getItem('token');
        const username = await AsyncStorage.getItem('username');
        const IpAddress = await AsyncStorage.getItem('IpAddress');

        // Create upload resumable for progress tracking
        const uploadTask = FileSystem.createUploadTask(
            `${process.env.REACT_NATIVE_API_BASE_URL}filehandler/upload`,
            fileUri,
            {
                httpMethod: 'POST',
                uploadType: FileSystem.FileSystemUploadType.MULTIPART,
                fieldName: 'file',
                parameters,
                headers: {
                    'authorization': 'Bearer ' + jwtToken,
                    'x-username': username || '',
                    'x-ip-address': IpAddress || '',
                },
            },
            (progressData) => {
                if (onProgress && progressData.totalBytesSent && progressData.totalBytesExpectedToSend) {
                    const percent = (progressData.totalBytesSent / progressData.totalBytesExpectedToSend) * 100;
                    onProgress(Math.round(percent));
                }
            }
        );

        const uploadResult = await uploadTask.uploadAsync();

        if (uploadResult && uploadResult.status >= 200 && uploadResult.status < 300) {
            const result = JSON.parse(uploadResult.body);
            return { ...result, status: uploadResult.status };
        } else {
            const status = uploadResult?.status || 0;
            return { error: `Upload failed: ${status}`, status };
        }
    } catch (error) {
        console.error('Error in uploadFileWithProgressNative:', error);
        return { error: `Exception during upload: ${error}`, status: 0 };
    }
};

/**
 * FUNCTION: downloadFileBinaryWeb
 * PURPOSE: Download file as binary blob (Web only)
 * @param route - API route
 * @param body - Request body
 * @returns Promise with blob and filename
 */
export const downloadFileBinaryWeb = async (
    route: string,
    body: any
): Promise<{ blob: Blob; filename: string; hash?: string } | { error: string }> => {
    try {
        // Get authentication token
        const jwtToken = await AsyncStorage.getItem('token');
        const username = await AsyncStorage.getItem('username');
        const IpAddress = await AsyncStorage.getItem('IpAddress');

        // Make request to get file
        const response = await fetch(
            `${process.env.REACT_NATIVE_API_BASE_URL}${route}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'authorization': 'Bearer ' + jwtToken,
                    'x-username': `${username}`,
                    'x-ip-address': `${IpAddress}`,
                },
                body: JSON.stringify(body)
            }
        );

        if (!response.ok) {
            return { error: `Download failed: ${response.statusText}` };
        }

        // Get filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'download';
        
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        // Get the blob
        const blob = await response.blob();
        
        // Get hash if provided
        const serverHash = response.headers.get('X-File-Hash');

        return { 
            blob, 
            filename,
            hash: serverHash || undefined
        };
    } catch (error) {
        console.error('Error in downloadFileBinaryWeb:', error);
        return { error: `Exception during download: ${error}` };
    }
};

/**
 * FUNCTION: downloadFileBinaryNative
 * PURPOSE: Download file to device (Native only) using POST request
 * @param route - API route
 * @param body - Request body with download parameters (docid, documentname, etc.)
 * @param fileUri - Local file URI to save to
 * @returns Promise with file URI and hash
 */
export const downloadFileBinaryNative = async (
    route: string,
    body: any,
    fileUri: string
): Promise<{ uri: string; hash?: string } | { error: string }> => {
    try {
        // Get authentication token
        const jwtToken = await AsyncStorage.getItem('token');
        const username = await AsyncStorage.getItem('username');
        const IpAddress = await AsyncStorage.getItem('IpAddress');

        // Use fetch to make POST request with body
        const response = await fetch(
            `${process.env.REACT_NATIVE_API_BASE_URL}${route}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/octet-stream, application/json',
                    'authorization': 'Bearer ' + jwtToken,
                    'x-username': username || '',
                    'x-ip-address': IpAddress || '',
                },
                body: JSON.stringify(body),
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => response.statusText);
            console.error('Download failed:', response.status, errorText);
            return { error: `Download failed (${response.status}): ${response.statusText}` };
        }

        // Get hash from response headers
        const hash = response.headers.get('x-file-hash') || response.headers.get('X-File-Hash');

        // Get the binary data as ArrayBuffer
        const arrayBuffer = await response.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        // Convert to base64 for FileSystem.writeAsStringAsync
        const base64 = Buffer.from(uint8Array).toString('base64');

        // Write to file system
        await FileSystem.writeAsStringAsync(fileUri, base64, {
            encoding: FileSystem.EncodingType.Base64,
        });

        return { uri: fileUri, hash: hash || undefined };
    } catch (error) {
        console.error('Error in downloadFileBinaryNative:', error);
        return { error: `Exception during download: ${error}` };
    }
};

/**
 * FUNCTION: attachFileToCharger
 * PURPOSE: Associate an uploaded file with a charger ID
 * @param attachObj - Attachment object with documentid and chargerid
 * @returns Promise resolving to 0 on success, error string on failure
 */
export const attachFileToCharger = async (
    attachObj: AttachmentObj
): Promise<number | string> => {
    try {
        const apiQueryObj: ApiRequest = {
            route: 'filehandler',
            request: {
                method: 'POST',
                entity: 'document',
                body: {
                    action: fileActions.ATTACH_TO_CHARGER_ID,
                    chargerid: attachObj.chargerid,
                    docid: attachObj.documentid,
                }
            }
        };
        
        const response = await fetchDataFromAPITyped<Array<{ 
            documentid?: number; 
            error?: string 
        }>>(apiQueryObj);
        
        if (!isSuccessful(response.status)) {
            console.error('Failed to attach file to charger:', response);
            return 'Attachment failed';
        }

        if (response.payload && response.payload[0]) {
            if (response.payload[0].documentid !== undefined) {
                attachObj.documentid = response.payload[0].documentid;
                return 0; // Success
            } else if (response.payload[0].error) {
                return response.payload[0].error;
            }
        }
        
        return 'Unknown error during attachment';
    } catch (error) {
        console.error("Error attaching file to charger:", error);
        return 'Exception during attachment';
    }
};

/**
 * FUNCTION: getFileList
 * PURPOSE: Get a list of files associated with a charger
 * @param chargerid - The charger ID
 * @returns Promise resolving to array of AttachmentObj or null on error
 */
export const getFileList = async (
    chargerid: string
): Promise<Array<AttachmentObj> | null> => {
    try {
        const apiQueryObj: ApiRequest = {
            route: 'filehandler',
            request: {
                method: 'POST',
                entity: 'document',
                body: {
                    action: fileActions.LIST_OF_ALL_FILES,
                    chargerid: chargerid,
                }
            }
        };
        // console.log('file list action:', fileActions.LIST_OF_ALL_FILES);
        const response = await fetchDataFromAPITyped<Array<AttachmentObj>>(apiQueryObj);
        // console.log('file list response:', response);
        if (!isSuccessful(response.status)) {
            console.error('Failed to get list of files:', response);
            return null;
        }

        return response.payload || [];
    } catch (error) {
        console.error('Error getting list of files:', error);
        return null;
    }
};

/**
 * FUNCTION: deleteFile
 * PURPOSE: Mark a file as deleted (soft delete)
 * @param attachObj - Attachment object with documentid
 * @returns Promise resolving to true on success, false on failure
 */
export const deleteFile = async (
    attachObj: AttachmentObj
): Promise<boolean> => {
    try {
        const apiQueryObj: ApiRequest = {
            route: 'filehandler',
            request: {
                method: 'POST',
                entity: 'document',
                body: {
                    action: fileActions.DELETE_FILE_OLD,
                    docid: attachObj.documentid,
                }
            }
        };
        
        const response = await fetchDataFromAPITyped<any>(apiQueryObj);
        
        if (!isSuccessful(response.status)) {
            console.error('Failed to delete file:', response);
            return false;
        }

        // Backend might return object or array, handle both
        const payload = response.payload;
        
        if (payload) {
            // If it's an object with success property
            if (typeof payload === 'object' && !Array.isArray(payload)) {
                if (payload.error) {
                    console.error('Error deleting file:', payload.error);
                    return false;
                }
                if (payload.success !== undefined) {
                    return payload.success;
                }
                // If no error and backend returned 200, consider it success
                return true;
            }
            
            // If it's an array
            if (Array.isArray(payload) && payload[0]) {
                if (payload[0].error) {
                    console.error('Error deleting file:', payload[0].error);
                    return false;
                }
                return payload[0].success || true;
            }
        }
        
        // If we got here with successful status, consider it a success
        return true;
    } catch (error) {
        console.error('Error deleting file from server:', error);
        return false;
    }
};

